import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PushNotifications } from "@capacitor/push-notifications";
import { isNative } from "../lib/platform.js";

/**
 * usePushDeepLink — Task C2.
 *
 * Native only: when the user taps a push notification, deep-link to the
 * relevant vehicle. The backend buildPayload
 * (backend/src/notifications/buildPayload.js:61) emits the deepLink as an
 * ABSOLUTE URL (`${PUBLIC_APP_URL}/?focus=device:<imei>&alertId=<id>`), shared
 * with web push. react-router's navigate() does NOT accept absolute
 * scheme+host URLs, so we convert the deepLink to a path+search RELATIVE
 * string with a same-origin guard before navigating. If the deepLink is
 * absent we fall back to a self-built `/?focus=device:<imei>` from
 * `data.deviceImei`. With neither (or a malformed/cross-origin deepLink),
 * navigate home.
 *
 * On web isNative() is false: the hook subscribes to nothing and is a no-op,
 * so web behaviour is byte-identical.
 */
export function usePushDeepLink() {
  const navigate = useNavigate();
  useEffect(() => {
    if (!isNative()) return undefined;
    const handle = PushNotifications.addListener(
      "pushNotificationActionPerformed",
      ({ notification }) => {
        const data = notification?.data || {};
        const fallback = data.deviceImei ? `/?focus=device:${data.deviceImei}` : "/";
        if (!data.deepLink) {
          navigate(fallback);
          return;
        }
        try {
          // `new URL(rel, origin)` resolves both absolute and relative deepLinks.
          const u = new URL(data.deepLink, window.location.origin);
          if (u.origin === window.location.origin) {
            navigate(u.pathname + u.search);
          } else {
            navigate("/"); // cross-origin deep-link — never trust it
          }
        } catch {
          navigate(fallback); // malformed deepLink — degrade gracefully
        }
      },
    );
    return () => {
      Promise.resolve(handle).then((h) => h?.remove?.());
    };
  }, [navigate]);
}
